import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { useEffect, useState } from "react";
import { P } from "theme/typography";
import { CollectUserView } from "generated/graphql";
import PCheckbox from "components/PCheckbox";
import { updateCollectUserLensPermission } from "redux/slices/client";
import { useAllowedLenses } from "helpers/hooks/useAllowedLenses";

interface Props {
  open: boolean;
  onClose: () => void;
  user: CollectUserView;
}

const CollectUserLensPicker = ({ open, onClose, user }: Props) => {
  const [loading, setLoading] = useState(false);
  const snackpatch = useAppDispatchWithSnackbar();
  const allowedLenses = useAllowedLenses(user.firms.map((f) => f.id));

  const [addLenses, setAddLenses] = useState<number[]>([]);
  const [removeLenses, setRemoveLenses] = useState<number[]>([]);

  // reset add/remove lenses when the user changes
  useEffect(() => {
    setAddLenses([]);
    setRemoveLenses([]);
  }, [user]);

  const handleSave = () => {
    const affectedLenses = addLenses.concat(removeLenses);
    setLoading(true);
    snackpatch(
      updateCollectUserLensPermission({
        collectUserId: user.id,
        lensPermissions: affectedLenses.map((lensId) => ({
          lensId: lensId,
          status: addLenses.includes(lensId)
            ? true
            : removeLenses.includes(lensId)
              ? false
              : user.lensIds.includes(lensId)
                ? true
                : false,
        })),
      }),
      {
        successMessage: "Lens permissions updated",
        errorMessage: "Unable to update lens permissions",
      }
    )
      .then(onClose)
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <P variant="h5">User Lenses</P>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "400px",
            mt: 2,
          }}>
          <P
            sx={{
              mb: 1,
            }}>
            Select lenses to grant to this user
          </P>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxHeight: "300px",
              overflow: "auto",
            }}>
            {allowedLenses.map((lens) => (
              <Box key={lens.id}>
                <PCheckbox
                  checked={
                    addLenses.includes(lens.id) || (!removeLenses.includes(lens.id) && user.lensIds.includes(lens.id))
                  }
                  label={lens.name}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // If the lens is not already attached to the user, add it to the addLenses array
                      if (!user.lensIds.includes(lens.id)) {
                        setAddLenses([...addLenses, lens.id]);
                      }
                      // If the lens is already attached to the user, remove it from the removeLenses array
                      else {
                        setRemoveLenses(removeLenses.filter((lensId) => lensId !== lens.id));
                      }
                    } else {
                      // If the lens is already attached to the user, add it to the removeLenses array
                      if (user.lensIds.includes(lens.id)) {
                        setRemoveLenses([...removeLenses, lens.id]);
                      }
                      // If the lens is not attached to the user, remove it from the addLenses array
                      else {
                        setAddLenses(addLenses.filter((lensId) => lensId !== lens.id));
                      }
                    }
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton loading={loading} onClick={handleSave} variant="contained">
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CollectUserLensPicker;
